import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { BASIC_ORDER_HISTORY_STATUSES } from 'common/constants/orderStatusGroups';
import useInfoDialog from 'common/hooks/useInfoDialog';
import AssociationDetailsTab from 'common/modules/AssociationDetailsTab';
import PatientBanner from 'common/modules/PatientBanner';
import orderEnum from 'enums/order.enum';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { getPatientOrderHistory, voidShipment } from 'store/thunks/patientThunks';
import { getPickupVoidRestriction } from 'common/utils/shipmentVoidRestriction';

const VoidShipmentButton = ({ mpi, orderId, lineItems, order, onSuccess }) => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const { InfoDialog, showInfoDialog } = useInfoDialog();

  // POM-5299 — once any shipment in the order is with the carrier, voiding is no longer
  // allowed (backend enforces the same rule; this disables the action up front).
  const { isPickedUp, tooltip: voidShipmentTooltip } = getPickupVoidRestriction(lineItems);

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = (_, reason) => {
    if (reason !== 'backdropClick') {
      setShowModal(false);
      setSelectedState(null);
    }
  };

  const handleReshipOrder = () => {
    if (selectedState) {
      const formattedState = selectedState.toLowerCase().replace(/_/g, ' ');
      showInfoDialog({
        title: 'Confirm Change Order Status',
        message: `Are you sure you want to change order status to ${formattedState}?`,
        callback: () => {
          setIsLoading(true);
          dispatch(
            voidShipment({
              mpi,
              orderId,
              lineItemIds: lineItems.map((item) => item.lineItemId),
              orderStatus: selectedState,
            })
          )
            .then((response) => {
              if (response?.voidShipment === true) {
                enqueueSnackbar(`Order Status changed to ${formattedState}`, {
                  variant: 'success',
                });
                onSuccess?.();
                dispatch(
                  getPatientOrderHistory({
                    mpi,
                    status: BASIC_ORDER_HISTORY_STATUSES,
                    includeAllFacilities: true,
                  })
                );
                setShowModal(false);
              }
            })
            .finally(() => setIsLoading(false));
        },
        cancelable: true,
        confirmButtonText: 'Yes',
      });
    }
  };

  return (
    <>
      <InfoDialog />
      <Dialog
        open={showModal}
        onClose={handleCloseModal}
        aria-describedby='Undo-Dialog-header'
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: { maxWidth: '700px' },
        }}
      >
        <Form
          onSubmit={handleReshipOrder}
          render={({ handleSubmit }) => (
            <form noValidate onSubmit={handleSubmit}>
              <DialogContent sx={{ minWidth: 500 }}>
                <Grid container sx={{ mb: 2 }}>
                  <Typography variant='h6'>Change Order Status</Typography>
                </Grid>
                <Grid container sx={{ mb: 2 }}>
                  <PatientBanner mpi={mpi} />
                </Grid>

                <Grid container sx={{ mb: 2 }}>
                  <AssociationDetailsTab orderId={orderId} patientId={mpi} order={order} />
                </Grid>
                <FormControl component='fieldset' sx={{ mb: 3, width: '100%' }}>
                  <FormLabel component='legend' sx={{ mb: 1.5, fontWeight: 600, fontSize: '1rem' }}>
                    Select Order Status
                  </FormLabel>
                  <RadioGroup
                    value={selectedState}
                    onChange={(e) => setSelectedState(e.target.value)}
                    sx={{
                      display: 'flex',
                      gap: 2,
                    }}
                  >
                    <Grid
                      container
                      alignItems='center'
                      sx={{
                        px: 2,
                        py: 1.2,
                        border: '1px solid',
                        borderColor:
                          selectedState === orderEnum.ORDER_RE_VERIFICATION
                            ? 'primary.main'
                            : 'grey.300',
                        borderRadius: 2,
                        cursor: 'pointer',
                        boxShadow: selectedState === orderEnum.ORDER_RE_VERIFICATION ? 2 : 0,
                        transition: 'all 0.2s ease-in-out',
                      }}
                      onClick={() => setSelectedState(orderEnum.ORDER_RE_VERIFICATION)}
                    >
                      <Radio
                        checked={selectedState === orderEnum.ORDER_RE_VERIFICATION}
                        value={orderEnum.ORDER_RE_VERIFICATION}
                        sx={{ mr: 1 }}
                      />
                      <Typography>
                        Back to <b>Order Reverification</b> state
                      </Typography>
                    </Grid>

                    <Grid
                      container
                      alignItems='center'
                      sx={{
                        px: 2,
                        py: 1.2,
                        border: '1px solid',
                        borderColor:
                          selectedState === orderEnum.ORDER_VERIFIED ? 'primary.main' : 'grey.300',
                        borderRadius: 2,
                        cursor: 'pointer',
                        boxShadow: selectedState === orderEnum.ORDER_VERIFIED ? 2 : 0,
                        transition: 'all 0.2s ease-in-out',
                      }}
                      onClick={() => setSelectedState(orderEnum.ORDER_VERIFIED)}
                    >
                      <Radio
                        checked={selectedState === orderEnum.ORDER_VERIFIED}
                        value={orderEnum.ORDER_VERIFIED}
                        sx={{ mr: 1 }}
                      />
                      <Typography>
                        Back to <b>Order verified</b> state
                      </Typography>
                    </Grid>

                    <Grid
                      container
                      alignItems='center'
                      sx={{
                        px: 2,
                        py: 1.2,
                        border: '1px solid',
                        borderColor:
                          selectedState === orderEnum.ADDED_TO_SHIPMENT
                            ? 'primary.main'
                            : 'grey.300',
                        borderRadius: 2,
                        cursor: 'pointer',
                        boxShadow: selectedState === orderEnum.ADDED_TO_SHIPMENT ? 2 : 0,
                        transition: 'all 0.2s ease-in-out',
                      }}
                      onClick={() => setSelectedState(orderEnum.ADDED_TO_SHIPMENT)}
                    >
                      <Radio
                        checked={selectedState === orderEnum.ADDED_TO_SHIPMENT}
                        value={orderEnum.ADDED_TO_SHIPMENT}
                        sx={{ mr: 1 }}
                      />
                      <Typography>
                        Back to <b>Shipment Created</b> state
                      </Typography>
                    </Grid>
                  </RadioGroup>
                </FormControl>
              </DialogContent>
              <DialogActions>
                <Button variant='outlined' color='secondary' onClick={handleCloseModal}>
                  Cancel
                </Button>
                <LoadingButton
                  loading={isLoading}
                  variant='contained'
                  type='submit'
                  disabled={!selectedState}
                >
                  Submit
                </LoadingButton>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
      <Tooltip title={voidShipmentTooltip} arrow placement='top'>
        <span>
          <Button
            variant='outlined'
            size='medium'
            sx={{ px: '4px', py: '3px', whiteSpace: 'nowrap', minWidth: 0 }}
            onClick={handleOpenModal}
            disabled={isPickedUp}
          >
            <Typography variant='small' color='secondary'>
              Void Shipment
            </Typography>
          </Button>
        </span>
      </Tooltip>
    </>
  );
};

VoidShipmentButton.propTypes = {
  mpi: PropTypes.string.isRequired,
  orderId: PropTypes.string.isRequired,
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      lineItemId: PropTypes.string.isRequired,
      pickedUpTimestamp: PropTypes.string,
      outForDeliveryTimestamp: PropTypes.string,
      deliveredTimestamp: PropTypes.string,
    })
  ).isRequired,
  order: PropTypes.shape({
    status: PropTypes.string,
    createdAt: PropTypes.string,
  }).isRequired,
  onSuccess: PropTypes.func,
};

VoidShipmentButton.defaultProps = {
  onSuccess: undefined,
};

export default VoidShipmentButton;
